// Analytics service that wraps the underlying provider

import type {
  AnalyticsProvider,
  AnalyticsEventName,
  AnalyticsEventPropertiesMap,
} from "../../types/analytics";
import { posthogProvider } from "../../providers/posthogProvider";

/**
 * Analytics Service class that wraps a provider implementation
 */
export class AnalyticsService implements AnalyticsProvider {
  private provider: AnalyticsProvider;

  /**
   * Create a new AnalyticsService
   *
   * @param provider - The analytics provider to use (PostHog, Amplitude, etc.)
   */
  constructor(provider: AnalyticsProvider) {
    this.provider = provider;
  }

  /**
   * Initialize the analytics provider
   */
  initialize(): void {
    this.provider.initialize();
  }

  /**
   * Track an analytics event
   *
   * @param event - The AnalyticsEventName
   * @param properties - The properties associated with the event
   */
  track<E extends AnalyticsEventName>(event: E, properties: AnalyticsEventPropertiesMap[E]): void {
    this.provider.track(event, properties);
  }

  /**
   * Identify a user
   *
   * @param userId - Unique identifier for the user
   * @param traits - User properties to set, if any
   */
  identify(userId: string, traits?: Record<string, unknown>): void {
    this.provider.identify(userId, traits);
  }

  /**
   * Reset user identification
   */
  reset(): void {
    this.provider.reset();
  }

  /**
   * Check if analytics is enabled
   */
  isEnabled(): boolean {
    return this.provider.isEnabled();
  }
}

// Singleton
export const analyticsService = new AnalyticsService(posthogProvider);
